import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {graphql, useFragment} from 'react-relay'

import type {IssueSidebarLazySections$key} from './__generated__/IssueSidebarLazySections.graphql'
import type {IssueViewerViewer$data} from './__generated__/IssueViewerViewer.graphql'
import {
  DevelopmentSection,
  DevelopmentSectionFallback,
  type DevelopmentSectionFragment,
} from './sections/development-section/DevelopmentSection'
import {ParticipantsSection} from './sections/ParticipantsSection'
import {
  RelationshipsSectionFallback,
  type RelationshipsSectionFragment,
  RelationshipsSectionInternal,
} from './sections/relations-section/RelationshipsSection'
import {SubscriptionSection, SubscriptionSectionFallback} from './sections/subscription-section/SubscriptionSection'

type IssueSidebarLazySectionsBaseProps = {
  viewer: IssueViewerViewer$data | null
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

type IssueSidebarLazySectionsProps = IssueSidebarLazySectionsBaseProps & {
  issueSidebarSecondaryKey?: IssueSidebarLazySections$key
}

export function IssueSidebarLazySections({
  issueSidebarSecondaryKey,
  onIssueUpdate,
  onLinkClick,
  insideSidePanel,
  viewer,
  readonly,
}: IssueSidebarLazySectionsProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueSidebarLazySections on Issue {
        # DevelopmentSectionFragment was inlined here when DevelopmentSection migrated off Relay.
        # The selection is read as plain props via DevelopmentSectionFragment
        repository {
          id
          databaseId
          name
          owner {
            login
          }
        }
        id
        title
        number
        databaseId
        linkedBranches(first: 25) {
          nodes {
            id
            ref {
              name
              id
              __typename
              target {
                oid
                id
              }
              repository {
                id
                nameWithOwner
                defaultBranchRef {
                  name
                  id
                  target {
                    oid
                    id
                  }
                  associatedPullRequests {
                    totalCount
                  }
                }
              }
              associatedPullRequests {
                totalCount
              }
            }
          }
        }
        closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
          nodes {
            ... on PullRequest {
              id
              url
              number
              title
              state
              isDraft
              isInMergeQueue
              createdAt
              repository {
                id
                name
                nameWithOwner
                owner {
                  id
                  login
                  __typename
                }
              }
              __typename
            }
          }
        }
        linkedPullRequestReleases(first: 10) {
          nodes {
            pullRequest {
              id
            }
            release {
              id
              tagName
              name
              url
              isPrerelease
              isLatest
            }
          }
        }
        viewerCanLinkBranches
        # Inlined from the former RelationshipsSectionFragment; read as plain props by RelationshipsSectionInternal.
        id
        databaseId
        viewerCanUpdateMetadata
        repository {
          nameWithOwner
          isArchived
          owner {
            login
          }
        }
        parent {
          id
          titleHTML
          url
          number
          repository {
            nameWithOwner
          }
          state
          stateReason
          subIssuesSummary {
            total
            completed
          }
        }
        topBlockedBy: blockedBy(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topBlocking: blocking(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        securityAlerts(first: 100) {
          nodes {
            number
            repository {
              nameWithOwner
            }
            status
            title
          }
        }
        issueDependenciesSummary {
          blockedBy
          blocking
        }
        # Inlined from the former SubscriptionSectionFragment; read as plain props by SubscriptionSection.
        threadSubscriptionChannel
        ...SubscriptionSectionRefetchableFragment
        repository {
          name
          owner {
            login
          }
          viewerCanPinIssues
        }
        number
        participants(first: 10) {
          nodes {
            login
            avatarUrl(size: 64)
            id
          }
          totalCount
        }
        viewerCanConvertToDiscussion
        viewerCanDelete
        viewerCanTransfer
        viewerCanType
        viewerCanLock
      }
    `,
    issueSidebarSecondaryKey,
  )

  if (!data) {
    return <IssueSidebarLazySectionsFallback />
  }

  // When the sticky header is enabled, the subscribe action is rendered there instead of in the sidebar.
  const showSubscriptionSection = !isFeatureEnabled('issues_sticky_header') && isLoggedIn()

  return (
    <>
      {/* RELAY-CLEANUP(IssueSidebarLazySections): `data` is still a Relay fragment ref carrying open-enum
          `state`/`stateReason` variants the server-persisted-query types omit. Remove this cast once
          IssueSidebarLazySections is migrated. */}
      <RelationshipsSectionInternal
        issue={data as RelationshipsSectionFragment}
        onLinkClick={onLinkClick}
        insideSidePanel={insideSidePanel}
        readonly={readonly}
        onSelection={onIssueUpdate}
      />
      {/* DevelopmentSection is migrated off Relay; `data` is still a Relay fragment ref here, so we
          cast it to the plain type. Remove the cast once IssueSidebarLazySections is migrated. */}
      <DevelopmentSection issue={data as DevelopmentSectionFragment} onSelection={onIssueUpdate} readonly={readonly} />
      {showSubscriptionSection && <SubscriptionSection issue={data} viewer={viewer} />}
      <ParticipantsSection issue={data} />
    </>
  )
}

export function IssueSidebarLazySectionsFallback() {
  const showSubscriptionSection = !isFeatureEnabled('issues_sticky_header') && isLoggedIn()

  return (
    <>
      <RelationshipsSectionFallback />
      <DevelopmentSectionFallback />
      {showSubscriptionSection && <SubscriptionSectionFallback />}
    </>
  )
}
